import React, { useEffect, useMemo, useState } from 'react';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonDatetime,
  IonHeader,
  IonItem,
  IonLabel,
  IonModal,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import Toaster from '@/components/common/Toaster';
import InspectionChecklist from './InspectionChecklist';
import { ChecklistItem } from '@/types/model/inspection';
import { ChecklistItemFormData, InspectionFormData, InspectionModalData } from '@/types/model/inspection-modal';
import { AddInspectionGroupLineItem, FileResponse } from '@/types/response/InspectionResultResponses';
import inspectionDataService from '@/services/data/InspectionDataService';
import lookupDataService from '@/services/data/LookupDataService';

interface SelectOption {
  id: number;
  name: string;
}

interface AddInspectionModalProps {
  isOpen: boolean;
  onDidDismiss: () => void;
  onSuccess: () => void;
  groups: SelectOption[];
  inspectors: SelectOption[];
  sites: SelectOption[];
  getLineItems: (groupId: number) => Promise<AddInspectionGroupLineItem[]>;
  globalFiles: FileResponse[];
  currentUserId: number | null;
  canEditOtherInspectors: boolean;
}

type Phase = 1 | 2 | 3;

const padTimeValue = (value: number): string => value.toString().padStart(2, '0');

interface TimeParts {
  hour: string;
  minute: string;
  meridiem: 'AM' | 'PM';
}

const getCurrentDate = (): string => new Date().toISOString().slice(0, 10);

const getCurrentTimeParts = (): TimeParts => {
  const now = new Date();
  const hours24 = now.getHours();
  const meridiem: 'AM' | 'PM' = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 || 12;

  return {
    hour: padTimeValue(hours12),
    minute: padTimeValue(now.getMinutes()),
    meridiem,
  };
};

const to24HourTime = (time: TimeParts): string => {
  const parsedHour = parseInt(time.hour, 10);
  const parsedMinute = parseInt(time.minute, 10);

  if (Number.isNaN(parsedHour) || Number.isNaN(parsedMinute)) {
    return '00:00';
  }

  let hour24 = parsedHour % 12;
  if (time.meridiem === 'PM') {
    hour24 += 12;
  }

  return `${padTimeValue(hour24)}:${padTimeValue(parsedMinute)}`;
};

const normalizeDateTime = (date: string, time: TimeParts): string => {
  return new Date(`${date}T${to24HourTime(time)}:00`).toISOString();
};

const getPreferredSiteDefault = (): number | null => {
  const sites = lookupDataService.getSites();
  const userPreferenceSiteId = lookupDataService.getUserPreferences()?.DefaultSiteId;
  if (userPreferenceSiteId) {
    const preferredSite = sites?.get(userPreferenceSiteId);
    if (preferredSite?.IsActive) {
      return preferredSite.Id;
    }
  }

  const contractDefaultSiteId = lookupDataService.getContractDetails()?.DefaultSiteId;
  if (contractDefaultSiteId) {
    const contractSite = sites?.get(contractDefaultSiteId);
    if (contractSite?.IsActive) {
      return contractSite.Id;
    }
  }

  return null;
};

// const getLineItemSiteDefault = (lineItem: AddInspectionGroupLineItem | null): number | null => {
//   if (!lineItem?.SiteId) {
//     return null;
//   }

//   const site = lookupDataService.getSites()?.get(lineItem.SiteId);
//   return site?.IsActive ? site.Id : null;
// };

const buildInitialChecklistFormData = (items: ChecklistItem[]): ChecklistItemFormData[] => {
  return items.map(item => ({
    id: item.Id,
    score: null,
    comment: '',
    attachments: [],
  }));
};

const AddInspectionModal: React.FC<AddInspectionModalProps> = ({
  isOpen,
  onDidDismiss,
  onSuccess,
  groups,
  inspectors,
  sites,
  getLineItems,
  globalFiles,
  currentUserId,
  canEditOtherInspectors,
}) => {
  const [phase, setPhase] = useState<Phase>(1);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [lineItems, setLineItems] = useState<AddInspectionGroupLineItem[]>([]);
  const [selectedLineItemId, setSelectedLineItemId] = useState<number | null>(null);
  const [inspectionDate, setInspectionDate] = useState<string>(getCurrentDate());
  const [inspectionTime, setInspectionTime] = useState<TimeParts>(getCurrentTimeParts());
  const [selectedInspectorId, setSelectedInspectorId] = useState<number | null>(currentUserId);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([]);
  const [checklistFormData, setChecklistFormData] = useState<ChecklistItemFormData[]>([]);
  const [loadingLineItems, setLoadingLineItems] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const selectedLineItem = useMemo(
    () => lineItems.find(item => item.Id === selectedLineItemId) ?? null,
    [lineItems, selectedLineItemId],
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setPhase(1);
    setSelectedGroupId(null);
    setLineItems([]);
    setSelectedLineItemId(null);
    setInspectionDate(getCurrentDate());
    setInspectionTime(getCurrentTimeParts());
    setSelectedInspectorId(currentUserId);
    setSelectedSiteId(getPreferredSiteDefault());
    setChecklistItems([]);
    setChecklistFormData([]);
    setLoadingLineItems(false);
    setSubmitting(false);
    setErrorMessage(null);
  }, [isOpen, currentUserId]);

  useEffect(() => {
    if (selectedGroupId === null) {
      setLineItems([]);
      setSelectedLineItemId(null);
      return;
    }

    let isMounted = true;
    setLoadingLineItems(true);
    setSelectedLineItemId(null);

    getLineItems(selectedGroupId)
      .then(items => {
        if (!isMounted) {
          return;
        }
        console.log('Fetched line items for group', selectedGroupId, items);
        setLineItems(items);
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }
        setLineItems([]);
        setErrorMessage('Failed to load line items.');
      })
      .finally(() => {
        if (isMounted) {
          setLoadingLineItems(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [selectedGroupId, getLineItems]);

  useEffect(() => {
    if (!selectedLineItem) {
      return;
    }

    const normalizedChecklistItems: ChecklistItem[] = selectedLineItem.ChecklistItems.map(item => ({
      ...item,
      Score: null,
      Comment: '',
      AttachmentCount: 0,
      Attachments: null,
    }));

    setChecklistItems(normalizedChecklistItems);
    setChecklistFormData(buildInitialChecklistFormData(normalizedChecklistItems));

    // const lineItemSiteId = getLineItemSiteDefault(selectedLineItem);
    // if (lineItemSiteId) {
    //   setSelectedSiteId(lineItemSiteId);
    //   return;
    // }

    setSelectedSiteId(currentValue => currentValue ?? getPreferredSiteDefault());
  }, [selectedLineItem]);

  const handleChecklistItemChange = (itemId: number, data: Partial<ChecklistItemFormData>) => {
    setChecklistFormData(previous => previous.map(item => (item.id === itemId ? { ...item, ...data } : item)));
  };

  const validatePhaseOne = (): boolean => {
    if (!selectedGroupId || !selectedLineItemId) {
      setErrorMessage('Select a group and line item before continuing.');
      return false;
    }

    return true;
  };

  const validatePhaseTwo = (): boolean => {
    if (!inspectionDate) {
      setErrorMessage('Date is required.');
      return false;
    }

    // const inspectionDateTime = new Date(`${inspectionDate}T${to24HourTime(inspectionTime)}:00`);
    // if (inspectionDateTime.getTime() > Date.now()) {
    //   setErrorMessage('Date cannot be in the future.');
    //   return false;
    // }

    if (!selectedInspectorId) {
      setErrorMessage('Inspector is required.');
      return false;
    }

    if (!selectedSiteId) {
      setErrorMessage('Site is required.');
      return false;
    }

    return true;
  };

  const validateChecklist = (): boolean => {
    for (const checklistItem of checklistItems) {
      const formItem = checklistFormData.find(item => item.id === checklistItem.Id);
      if (!formItem) {
        setErrorMessage('Each checklist item must have a score between 0 and 10 or be marked N/A.');
        return false;
      }

      if (formItem.score !== null && (formItem.score < 0 || formItem.score > 10)) {
        setErrorMessage('Each checklist item must have a score between 0 and 10 or be marked N/A.');
        return false;
      }
    }

    return true;
  };

const handleSubmit = async () => {
    if (!selectedLineItemId || !selectedSiteId) {
      setErrorMessage('Line item and site are required.');
      return;
    }

    if (!validatePhaseTwo() || !validateChecklist()) {
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);

    try {
      const inspectionFormData: InspectionFormData = {
        comments: '',
        isExceptional: false,
        checklistItems: checklistFormData,
        attachments: [],
      };

      const inspectionData: InspectionModalData = {
        ...generratedInspectionModalData,
        lineItemId: selectedLineItemId,
        lineItem: selectedLineItem?.LineItem ?? '',
        title: selectedLineItem?.Title ?? '',
        siteId: selectedSiteId,
        siteName: sites.find(site => site.id === selectedSiteId)?.name ?? '',
        userId: selectedInspectorId,
        inspectorName: inspectors.find(inspector => inspector.id === selectedInspectorId)?.name ?? '',
        inspectionDate: normalizeDateTime(inspectionDate, inspectionTime),
      };
      
      const adjustedInspectionFormData: InspectionFormData = {
        ...inspectionFormData,
        checklistItems: inspectionFormData.checklistItems.map(item => ({
          ...item,
          id: 0,
          checklistItemId: item.id,
        })),
      };

      console.log('Submitting new inspection with data:', { inspectionData, adjustedInspectionFormData });

      await inspectionDataService.createInspection(
        inspectionData,
        adjustedInspectionFormData,
      );

      onSuccess();
    } catch (error) {
      console.error('Failed to add inspection:', error);
      setErrorMessage('Failed to add inspection.');
    } finally {
      setSubmitting(false);
    }
  };

    const generratedInspectionModalData: InspectionModalData = {
    inspectionResultId: 0,
    title: '',
    inspectionDate: '',
    userId: null,
    inspectorName: '',
    siteName: '',
    siteId: 0,
    lineItem: '',
    lineItemId: 0,
    isExceptional: null,
    checklistItems: [],
    correctiveActions: [],
    existingResources: [],
    lineItemDetail: {
      PerformanceStandard: null,
      MajorRequirement: null,
      RelatedInfo: null,
      EstimatedQuantity: null,
      LineItemAttachments: [],
    },
    inspectionPlanBackground: {
      LineItemPrimaryWeight: null,
      LineItemAlternateWeight: null,
      TimeToInspect: null,
      MethodOfInspection: null,
      SampleSize: null,
      SampleSelection: null,
      AdditionalInstructions: null,
      InspectionPlanAttachments: [],
    },
    timeSensitivity: 'unknown',
    status: 'unknown',
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onDidDismiss} backdropDismiss={false}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>New Inspection</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onDidDismiss}>Close</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {phase === 1 && (
          <div style={{ padding: '16px' }}>
            <IonItem>
              <IonLabel position="stacked">Group</IonLabel>
              <IonSelect
                value={selectedGroupId}
                placeholder="Select group"
                onIonChange={event => setSelectedGroupId(event.detail.value ?? null)}
              >
                {groups.map(group => (
                  <IonSelectOption key={group.id} value={group.id}>{group.name}</IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Line Item</IonLabel>
              <IonSelect
                value={selectedLineItemId}
                placeholder={loadingLineItems ? 'Loading line items...' : selectedGroupId && lineItems.length === 0 ? 'No line items found' : 'Select line item'}
                disabled={!selectedGroupId || loadingLineItems || lineItems.length === 0}
                onIonChange={event => setSelectedLineItemId(event.detail.value ?? null)}
              >
                {lineItems.map(lineItem => (
                  <IonSelectOption key={lineItem.Id} value={lineItem.Id}>
                    {lineItem.LineItem} {lineItem.Title}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
              <IonButton fill="outline" onClick={onDidDismiss}>Cancel</IonButton>
              <IonButton onClick={() => {
                if (validatePhaseOne()) {
                  setErrorMessage(null);
                  setPhase(2);
                }
              }}>
                Next
              </IonButton>
            </div>
          </div>
        )}

        {phase === 2 && (
          <div style={{ padding: '16px' }}>
            <IonItem>
              <IonLabel position="stacked">Date</IonLabel>
              <IonDatetime
                value={inspectionDate}
                presentation="date"
                onIonChange={event => setInspectionDate(String(event.detail.value ?? '').slice(0, 10))}
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Time</IonLabel>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', width: '100%', paddingTop: '8px' }}>
                <IonSelect
                  value={inspectionTime.hour}
                  placeholder="Hour"
                  onIonChange={event => setInspectionTime(current => ({ ...current, hour: String(event.detail.value ?? '12') }))}
                >
                  {Array.from({ length: 12 }, (_, index) => {
                    const value = padTimeValue(index + 1);
                    return <IonSelectOption key={value} value={value}>{value}</IonSelectOption>;
                  })}
                </IonSelect>
                <IonSelect
                  value={inspectionTime.minute}
                  placeholder="Minute"
                  onIonChange={event => setInspectionTime(current => ({ ...current, minute: String(event.detail.value ?? '00') }))}
                >
                  {Array.from({ length: 60 }, (_, index) => {
                    const value = padTimeValue(index);
                    return <IonSelectOption key={value} value={value}>{value}</IonSelectOption>;
                  })}
                </IonSelect>
                <IonSelect
                  value={inspectionTime.meridiem}
                  placeholder="AM/PM"
                  onIonChange={event => setInspectionTime(current => ({ ...current, meridiem: event.detail.value === 'PM' ? 'PM' : 'AM' }))}
                >
                  <IonSelectOption value="AM">AM</IonSelectOption>
                  <IonSelectOption value="PM">PM</IonSelectOption>
                </IonSelect>
              </div>
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Inspector</IonLabel>
              <IonSelect
                value={selectedInspectorId}
                placeholder="Select inspector"
                disabled={!canEditOtherInspectors}
                onIonChange={event => setSelectedInspectorId(event.detail.value ?? null)}
              >
                {inspectors.map(inspector => (
                  <IonSelectOption key={inspector.id} value={inspector.id}>{inspector.name}</IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Site</IonLabel>
              <IonSelect
                value={selectedSiteId}
                placeholder="Select site"
                onIonChange={event => setSelectedSiteId(event.detail.value ?? null)}
              >
                {sites.map(site => (
                  <IonSelectOption key={site.id} value={site.id}>{site.name}</IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginTop: '20px' }}>
              <IonButton fill="outline" onClick={() => setPhase(1)}>Back</IonButton>
              <div style={{ display: 'flex', gap: '8px' }}>
                <IonButton fill="outline" onClick={onDidDismiss}>Cancel</IonButton>
                <IonButton onClick={() => {
                  if (validatePhaseTwo()) {
                    setErrorMessage(null);
                    setPhase(3);
                  }
                }}>
                  Next
                </IonButton>
              </div>
            </div>
          </div>
        )}

        {phase === 3 && (
          <div style={{ padding: '16px' }}>
            {selectedLineItem && (
              <div style={{ marginBottom: '12px' }}>
                <IonText color="medium">
                  <p style={{ margin: 0 }}>{selectedLineItem.LineItem} {selectedLineItem.Title}</p>
                </IonText>
              </div>
            )}

            <InspectionChecklist
              checklistItems={checklistItems}
              globalFiles={globalFiles}
              formData={checklistFormData}
              onItemChange={handleChecklistItemChange}
            />

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginTop: '20px' }}>
              <IonButton fill="outline" onClick={() => setPhase(2)} disabled={submitting}>Back</IonButton>
              <div style={{ display: 'flex', gap: '8px' }}>
                <IonButton fill="outline" onClick={onDidDismiss} disabled={submitting}>Cancel</IonButton>
                <IonButton onClick={handleSubmit} disabled={submitting}>
                  {submitting ? <IonSpinner name="crescent" /> : 'Submit'}
                </IonButton>
              </div>
            </div>
          </div>
        )}
      </IonContent>

      <Toaster
        isOpen={errorMessage !== null}
        message={errorMessage ?? ''}
        variant="error"
        onDidDismiss={() => setErrorMessage(null)}
      />
    </IonModal>
  );
};

export default AddInspectionModal;
